import { useState } from "react";
import {
    Burger,
    MobileMenu,
    Navbar,
    NavItem,
    NavLinks,
    NavLogo,
    NavWrapper,
    FacebookIcon,
    InstagramIcon,
    NavIcons,
    LangSwitch,
    LangBtn,
    CenterFlex,
    HomeIconSvg,
    NavRouterItem,
    NavRight,
    MobileNavIcons,
    SocialLinkIcon,
    BookingIcon,
} from "./styled";
import { Link } from "react-scroll";
import {
    Link as RouterLink,
    useLocation,
    useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LINKS, getLocalizedRoute } from "../../config/links";

const NavigationBar = ({ variant = "home" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { t, i18n } = useTranslation(["common", "policy"]);

    const navigate = useNavigate();
    const { pathname } = useLocation();
    const navItems = LINKS.navigation[variant] ?? [];
    const current = i18n.resolvedLanguage || i18n.language;

    const setLang = async (lng) => {
        const normalizedPath =
            pathname === "/"
                ? "/"
                : pathname.replace(/\/+$/, "");

        const currentRoute = Object.values(LINKS.routes).find(
            (route) =>
                route?.pl === normalizedPath ||
                route?.en === normalizedPath,
        );

        const targetPath =
            currentRoute?.[lng] ??
            LINKS.routes.home[lng];

        await i18n.changeLanguage(lng);
        navigate(targetPath, {
            state: {
                preserveScroll: true,
            },
        });

        setIsOpen(false);
    };

    return (
        <header>
            <Navbar>
                <NavWrapper>
                    {variant === "home" ? (
                        <Link to={LINKS.sections.home} smooth duration={600} offset={-90}>
                            <NavLogo aria-hidden="true" focusable="false" />
                        </Link>
                    ) : (
                        <RouterLink
                            to={getLocalizedRoute(LINKS.routes.home, current)}
                            aria-label="Czorsztynianka – strona główna"
                        >
                            <NavLogo aria-hidden="true" focusable="false" />
                        </RouterLink>
                    )}

                    {variant !== "policy" && (
                        <NavLinks>
                            {navItems.map((item) =>
                                item.type === "route" ? (
                                    <NavRouterItem
                                        key={item.key}
                                        to={getLocalizedRoute(LINKS.routes[item.to], current)}
                                    >
                                        {t(`nav.${item.key}`)}
                                    </NavRouterItem>
                                ) : (
                                    <NavItem
                                        key={item.key}
                                        to={item.to}
                                        smooth
                                        duration={600}
                                        offset={item.desktopOffset}
                                    >
                                        {t(`nav.${item.key}`)}
                                    </NavItem>
                                )
                            )}
                        </NavLinks>
                    )}

                    {variant === "policy" && (
                        <NavLinks>
                            <NavRouterItem
                                to={getLocalizedRoute(LINKS.routes.home, current)}
                                onClick={() => setIsOpen(false)}
                            >
                                <CenterFlex>
                                    <HomeIconSvg />
                                    {t("backHome", { ns: "policy" })}
                                </CenterFlex>
                            </NavRouterItem>
                        </NavLinks>
                    )}

                    <NavRight>
                        <LangSwitch>
                            <LangBtn
                                type="button"
                                $active={current?.startsWith("pl")}
                                onClick={() => setLang("pl")}
                                aria-label="Switch language to Polish"
                                title="Polski"
                            >
                                PL
                            </LangBtn>
                            <LangBtn
                                type="button"
                                $active={current?.startsWith("en")}
                                onClick={() => setLang("en")}
                                aria-label="Switch language to English"
                                title="English"
                            >
                                EN
                            </LangBtn>
                        </LangSwitch>

                        <NavIcons>
                            <SocialLinkIcon href={LINKS.external.facebook} target="_blank" aria-label="Facebook" rel="noreferrer">
                                <FacebookIcon />
                            </SocialLinkIcon>
                            <SocialLinkIcon href={LINKS.external.instagram} target="_blank" aria-label="Instagram" rel="noreferrer">
                                <InstagramIcon />
                            </SocialLinkIcon>
                            <SocialLinkIcon href={LINKS.external.housesBooking} target="_blank" aria-label="Instagram" rel="noreferrer">
                                <BookingIcon />
                            </SocialLinkIcon>
                        </NavIcons>

                        <Burger
                            $open={isOpen}
                            onClick={() => setIsOpen((v) => !v)}
                            aria-label={isOpen ? "Zamknij menu" : "Otwórz menu"}
                            aria-expanded={isOpen}
                            aria-controls="mobile-menu"
                        >
                            <span />
                            <span />
                            <span />
                        </Burger>
                    </NavRight>

                    <MobileMenu id="mobile-menu" $open={isOpen}>
                        {variant !== "policy" &&
                            navItems.map((item) =>
                                item.type === "route" ? (
                                    <NavRouterItem
                                        key={`mobile-${item.key}`}
                                        to={getLocalizedRoute(LINKS.routes[item.to], current)}
                                        onClick={() => setIsOpen(false)}
                                    >
                                        {t(`nav.${item.key}`)}
                                    </NavRouterItem>
                                ) : (
                                    <NavItem
                                        key={`mobile-${item.key}`}
                                        to={item.to}
                                        smooth
                                        duration={600}
                                        offset={item.mobileOffset}
                                        onClick={() => setIsOpen(false)}
                                    >
                                        {t(`nav.${item.key}`)}
                                    </NavItem>
                                )
                            )}

                        {variant === "policy" && (
                            <NavRouterItem
                                to={getLocalizedRoute(LINKS.routes.home, current)}
                                onClick={() => setIsOpen(false)}
                            >
                                <CenterFlex>
                                    <HomeIconSvg />
                                    {t("backHome", { ns: "policy" })}
                                </CenterFlex>
                            </NavRouterItem>
                        )}

                        <MobileNavIcons>
                            <SocialLinkIcon
                                href={LINKS.external.facebook}
                                target="_blank"
                                aria-label="Facebook"
                                rel="noreferrer"
                                onClick={() => setIsOpen(false)}
                            >
                                <FacebookIcon />
                            </SocialLinkIcon>
                            <SocialLinkIcon
                                href={LINKS.external.instagram}
                                target="_blank"
                                aria-label="Instagram"
                                rel="noreferrer"
                                onClick={() => setIsOpen(false)}
                            >
                                <InstagramIcon />
                            </SocialLinkIcon>
                            <SocialLinkIcon
                                href={LINKS.external.housesBooking}
                                target="_blank"
                                aria-label="Booking"
                                rel="noreferrer"
                                onClick={() => setIsOpen(false)}
                            >
                                <BookingIcon />
                            </SocialLinkIcon>
                        </MobileNavIcons>
                    </MobileMenu>
                </NavWrapper>
            </Navbar>
        </header>
    );
};

export default NavigationBar;